import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Plus, X, Users, Check } from "lucide-react";
import { api, errMsg } from "../lib/api";
import { PageHeader, Spinner } from "../components/common";

export default function Split() {
  const nav = useNavigate();
  const [total, setTotal] = useState("");
  const [note, setNote] = useState("");
  const [includeMe, setIncludeMe] = useState(true);
  const [people, setPeople] = useState([""]);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const setPerson = (i, v) => setPeople(people.map((p, idx) => idx === i ? v : p));
  const addPerson = () => setPeople([...people, ""]);
  const removePerson = (i) => setPeople(people.filter((_, idx) => idx !== i));

  const tags = people.map((p) => p.trim()).filter(Boolean);
  const parts = tags.length + (includeMe ? 1 : 0);
  const perShare = total && parts ? (parseFloat(total) / parts) : 0;

  const submit = async () => {
    if (!total || parseFloat(total) <= 0) { toast.error("Enter a total amount"); return; }
    if (tags.length === 0) { toast.error("Add at least one person's Cash Tag"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/splits", { total: parseFloat(total), cash_tags: tags, note: note || null, include_me: includeMe });
      setResult(data);
      toast.success("Split requests sent");
    } catch (e) { toast.error(errMsg(e)); }
    finally { setBusy(false); }
  };

  if (result) {
    return (
      <div className="animate-fade-up text-center pt-8">
        <div className="mx-auto grid place-items-center w-20 h-20 rounded-full bg-success/10 text-success"><Check size={44} /></div>
        <h1 className="font-display text-3xl font-light tracking-tight mt-6">Split sent</h1>
        <p className="text-muted-foreground mt-2">Total {result.total_display} · {result.requests.length} request(s) of {result.share_display}</p>
        <div className="rounded-2xl bg-card border border-border divide-y divide-border mt-6 text-left">
          {result.requests.map((r) => (
            <div key={r.cash_tag} className="flex justify-between items-center p-4">
              <div><div className="font-medium">{r.display_name}</div><div className="text-xs text-muted-foreground font-mono">{r.cash_tag}</div></div>
              <span className="font-mono font-semibold">{r.amount_display}</span>
            </div>
          ))}
        </div>
        <div className="space-y-3 mt-8">
          <button data-testid="split-view-requests" onClick={() => nav("/app/requests")} className="w-full h-14 rounded-2xl bg-primary text-primary-foreground font-semibold">View requests</button>
          <button data-testid="split-done" onClick={() => nav("/app")} className="w-full h-14 rounded-2xl bg-secondary font-semibold">Done</button>
        </div>
      </div>
    );
  }

  return (
    <div className="animate-fade-up">
      <PageHeader title="Split a bill" subtitle="Share a cost and request each person's part" />

      <div className="rounded-3xl bg-secondary/60 p-6 mb-5 text-center">
        <label className="text-xs font-bold tracking-wide uppercase text-muted-foreground">Total amount</label>
        <div className="relative mt-2">
          <span className="absolute left-1/2 -translate-x-[70px] top-1/2 -translate-y-1/2 text-3xl font-light text-muted-foreground">$</span>
          <input data-testid="split-total-input" type="number" step="0.01" value={total} onChange={(e) => setTotal(e.target.value)} placeholder="0.00"
            className="w-full bg-transparent text-center amount-figure text-4xl outline-none" />
        </div>
        {perShare > 0 && <p className="text-sm text-muted-foreground mt-2">≈ ${perShare.toFixed(2)} each across {parts} {parts === 1 ? "person" : "people"}</p>}
      </div>

      <label className="flex items-center justify-between py-3 px-4 rounded-2xl bg-card border border-border mb-4">
        <span className="flex items-center gap-2 text-sm font-medium"><Users size={18} className="text-primary" /> Include myself in the split</span>
        <input data-testid="split-include-me" type="checkbox" checked={includeMe} onChange={(e) => setIncludeMe(e.target.checked)} className="w-5 h-5 accent-[#4B23CC]" />
      </label>

      <div className="space-y-2 mb-3">
        {people.map((p, i) => (
          <div key={i} className="flex gap-2">
            <input data-testid={`split-person-${i}`} value={p} onChange={(e) => setPerson(i, e.target.value)} placeholder="$cashtag"
              className="flex-1 h-13 py-3.5 px-4 rounded-2xl bg-secondary outline-none font-mono" />
            {people.length > 1 && <button data-testid={`split-remove-${i}`} onClick={() => removePerson(i)} className="grid place-items-center w-12 rounded-2xl bg-secondary text-muted-foreground"><X size={18} /></button>}
          </div>
        ))}
      </div>
      <button data-testid="split-add-person" onClick={addPerson} className="flex items-center gap-2 text-sm font-semibold text-primary mb-5"><Plus size={16} /> Add person</button>

      <input data-testid="split-note-input" value={note} onChange={(e) => setNote(e.target.value)} maxLength={140} placeholder="What's it for? (optional)"
        className="w-full h-13 py-3.5 px-4 rounded-2xl bg-secondary outline-none mb-5" />

      <button data-testid="split-submit-btn" onClick={submit} disabled={busy}
        className="w-full h-14 rounded-2xl bg-primary text-primary-foreground font-semibold flex items-center justify-center shadow-[0_12px_32px_rgba(75,35,204,0.3)] disabled:opacity-60">
        {busy ? <Spinner className="w-5 h-5" /> : "Send split requests"}
      </button>
    </div>
  );
}
